import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", category: "Geral", sku: "", quantity: 0, min_quantity: 0, location: "", description: "" };

export default function Inventory() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const r = await api.get("/inventory");
      setItems(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...empty, ...item }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, quantity: Number(form.quantity), min_quantity: Number(form.min_quantity) };
      if (editing) await api.patch(`/inventory/${editing.id}`, payload);
      else await api.post("/inventory", payload);
      toast.success(editing ? "Item atualizado" : "Item criado");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (item) => {
    if (!confirm(`Excluir ${item.name}?`)) return;
    try { await api.delete(`/inventory/${item.id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const filtered = items.filter((i) =>
    !search || i.name.toLowerCase().includes(search.toLowerCase())
    || (i.sku || "").toLowerCase().includes(search.toLowerCase())
    || (i.category || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-8" data-testid="inventory-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Consumíveis</div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight">Estoque</h1>
          <p className="mt-2 text-sm text-muted-foreground">Controle de peças, consumíveis e materiais.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="new-item-btn"><Plus className="mr-2 h-4 w-4" /> Novo item</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Editar item" : "Novo item"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nome</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="item-name-input" /></div>
                <div><Label>Categoria</Label>
                  <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="item-category-input" /></div>
                <div><Label>SKU</Label>
                  <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="item-sku-input" /></div>
                <div><Label>Quantidade</Label>
                  <Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="item-qty-input" /></div>
                <div><Label>Mínimo</Label>
                  <Input type="number" value={form.min_quantity} onChange={(e) => setForm({ ...form, min_quantity: e.target.value })} data-testid="item-min-input" /></div>
                <div className="col-span-2"><Label>Localização</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="item-location-input" /></div>
                <div className="col-span-2"><Label>Descrição</Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="item-desc-input" /></div>
              </div>
              <DialogFooter><Button type="submit" data-testid="item-save-btn">Salvar</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Buscar por nome, SKU, categoria..."
          value={search} onChange={(e) => setSearch(e.target.value)} data-testid="inventory-search" />
      </div>

      <div className="overflow-hidden rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item</TableHead>
              <TableHead className="hidden md:table-cell">Categoria</TableHead>
              <TableHead className="hidden md:table-cell">SKU</TableHead>
              <TableHead>Quantidade</TableHead>
              <TableHead className="hidden lg:table-cell">Localização</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Nenhum item.</TableCell></TableRow>
            ) : filtered.map((i) => {
              const low = i.quantity <= i.min_quantity;
              return (
                <TableRow key={i.id} data-testid={`inventory-row-${i.id}`}>
                  <TableCell className="font-medium">{i.name}</TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">{i.category}</TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground font-mono text-xs">{i.sku || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-mono">{i.quantity}</span>
                      {low && <Badge className="bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300">Baixo</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground">{i.location || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(i)} data-testid={`edit-item-${i.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {user.role === "admin" && (
                      <Button size="icon" variant="ghost" onClick={() => remove(i)} data-testid={`delete-item-${i.id}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
